import { useCloudApi } from '@/api/cloudApi'
import { useOrganization } from '@/composables/useOrganization'
import type {
  CloudV1alpha1PoolRef,
  V1ObjectMeta,
  V1alpha1PulsarInstance,
  V1alpha1Instance
} from '@streamnative/cloud-api-client-typescript'
import { i18n } from '@/lang'
const { t } = i18n.global

export async function fetchInstances(namespace: string) {
  try {
    const { isInstanceEnabled } = useOrganization()
    const api = useCloudApi()

    if (isInstanceEnabled.value) {
      return (await api.listNamespacedInstance(namespace)).data
    } else {
      return (await api.listNamespacedPulsarInstance(namespace)).data
    }
  } catch (e) {
    throw Error(getErrorMessage(e, 'fetchInstance Error'))
  }
}

export async function fetchInstance(instance: string, namespace: string) {
  try {
    const api = useCloudApi()
    const res = await api.readNamespacedPulsarInstance(instance, namespace)
    return res.data
  } catch (e) {
    throw Error(getErrorMessage(e, 'fetchInstance Error'))
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function deleteInstance(payload: any) {
  try {
    const api = useCloudApi()
    if (payload.instanceType === 'pulsar') {
      const res = await api.deleteNamespacedPulsarInstance(
        payload.instanceName,
        payload.organization
      )
      return res.data
    } else {
      const res = await api.deleteNamespacedInstance(payload.instanceName, payload.organization)
      return res.data
    }
  } catch (e) {
    throw Error(getErrorMessage(e))
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createInstance(payload: any) {
  const api = useCloudApi()
  const { isInstanceEnabled } = useOrganization()

  if (!payload.poolRef) {
    const { data: listPooloptionData } = await api.listNamespacedPoolOption(payload.organization)
    const poolOptionItem = listPooloptionData.items.find(item => {
      return (
        item.spec?.cloudType === payload.cloudProvider &&
        item.spec?.locations !== undefined &&
        item.spec?.locations.length > 0
      )
    })
    const poolRef: CloudV1alpha1PoolRef | undefined = poolOptionItem
      ? poolOptionItem.spec?.poolRef
      : undefined
    if (poolRef === undefined) {
      throw Error(t('instance.errorCreating'))
    }
    payload.poolRef = poolRef
  }

  // If organization doesn't have instance-enabled annotation, create PulsarInstance for backward compatibility
  if (!isInstanceEnabled.value) {
    const pulsarInstance: V1alpha1PulsarInstance & { metadata: V1ObjectMeta } = {
      kind: 'PulsarInstance',
      metadata: {
        name: payload.instance,
        namespace: payload.organization
      },
      spec: {
        availabilityMode: payload.availabilityMode,
        type: payload.type ? payload.type.toLowerCase() : undefined,
        poolRef: payload.poolRef
      }
    }
    // Note: Engine annotation is set later when creating the cluster,
    // as isURSAFeaturePossible and clusterProfile are not known at instance creation time.

    const { data } = await api.createNamespacedPulsarInstance(payload.organization, pulsarInstance)
    return data
  }

  // Create new Instance type for organizations with instance-enabled annotation
  const instance: V1alpha1Instance & { metadata: V1ObjectMeta } = {
    kind: 'Instance',
    metadata: {
      name: payload.instance,
      namespace: payload.organization
    },
    spec: {
      // https://github.com/streamnative/cloud-api-server/pull/4168
      // availabilityMode: payload.availabilityMode,
      type: payload.type ? payload.type.toLowerCase() : undefined,
      poolRef: payload.poolRef
    }
  }
  // Note: Engine annotation is set later when creating the cluster,
  // as isURSAFeaturePossible and clusterProfile are not known at instance creation time.

  const { data } = await api.createNamespacedInstance(payload.organization, instance)

  return data
}

export interface UpdateInstancePayload {
  instanceName: string
  organization: string
  clusterType: 'pulsar' | 'kafka'
  annotations?: Record<string, string>
  /** If true, merge with existing annotations; if false, replace all annotations */
  mergeAnnotations?: boolean
}

/**
 * Update instance annotations
 * @param payload - Update payload
 * @param payload.mergeAnnotations - If true (default), preserves existing annotations and only updates specified ones.
 *                                   If false, replaces all annotations with the new ones.
 */
export async function updateInstanceAnnotations(payload: UpdateInstancePayload) {
  const api = useCloudApi()

  if (!payload.instanceName || !payload.organization) {
    throw Error(t('instance.errorUpdating') || 'Instance name and organization are required')
  }

  if (!payload.annotations || Object.keys(payload.annotations).length === 0) {
    throw Error('No annotations to update')
  }

  const mergeAnnotations = payload.mergeAnnotations !== false

  try {
    let newAnnotations = payload.annotations

    if (mergeAnnotations) {
      let currentInstance: V1alpha1PulsarInstance | V1alpha1Instance
      // Read current instance to get existing annotations
      if (payload.clusterType === 'pulsar') {
        const { data } = await api.readNamespacedPulsarInstance(
          payload.instanceName,
          payload.organization
        )
        currentInstance = data as V1alpha1PulsarInstance
      } else {
        const { data } = await api.readNamespacedInstance(
          payload.instanceName,
          payload.organization
        )
        currentInstance = data as V1alpha1Instance
      }
      const existingAnnotations = currentInstance?.metadata?.annotations || {}
      // Merge existing annotations with new ones (new ones take precedence)
      newAnnotations = { ...existingAnnotations, ...payload.annotations }
    }

    // Use JSON Patch to update the entire annotations object
    // Note: We patch the whole annotations object, so key escaping is not needed here.
    // If patching individual keys, use: `/metadata/annotations/${key.replace(/~/g, '~0').replace(/\//g, '~1')}`
    const patchArray: Record<string, unknown>[] = [
      {
        op: 'add',
        path: '/metadata/annotations',
        value: newAnnotations
      }
    ]

    if (payload.clusterType === 'pulsar') {
      const { data } = await api.patchNamespacedPulsarInstance(
        payload.instanceName,
        payload.organization,
        patchArray
      )
      return data
    } else {
      const { data } = await api.patchNamespacedInstance(
        payload.instanceName,
        payload.organization,
        patchArray
      )
      return data
    }
  } catch (e) {
    throw Error(getErrorMessage(e, 'updateInstance Error'))
  }
}
